const SESSION_CHANNEL_NAME = "bondit_session";
const LOGOUT_STORAGE_KEY = "bondit_logout_at";

interface LogoutMessage {
  type: "logout";
}

const LOGOUT_MESSAGE: LogoutMessage = { type: "logout" };

const isLogoutMessage = (value: unknown): value is LogoutMessage =>
  typeof value === "object" && value !== null && (value as { type?: unknown }).type === "logout";

/**
 * Notify other same-origin tabs that the user explicitly logged out. The
 * message contains no credential or user data. Older browsers use a
 * timestamp-only localStorage event as a fallback.
 */
export const publishSessionLogout = (): void => {
  if (typeof BroadcastChannel !== "undefined") {
    try {
      const channel = new BroadcastChannel(SESSION_CHANNEL_NAME);
      channel.postMessage(LOGOUT_MESSAGE);
      channel.close();
      return;
    } catch {
      // Fall through to the storage event when BroadcastChannel construction
      // is unavailable despite the global being present.
    }
  }

  localStorage.setItem(LOGOUT_STORAGE_KEY, String(Date.now()));
};

/** Subscribe to logout events from other same-origin tabs. */
export const subscribeToSessionLogout = (onLogout: () => void): (() => void) => {
  if (typeof BroadcastChannel !== "undefined") {
    try {
      const channel = new BroadcastChannel(SESSION_CHANNEL_NAME);
      const handleMessage = (event: MessageEvent<unknown>) => {
        if (isLogoutMessage(event.data)) {
          onLogout();
        }
      };

      channel.addEventListener("message", handleMessage);
      return () => {
        channel.removeEventListener("message", handleMessage);
        channel.close();
      };
    } catch {
      // Fall through to the storage event when BroadcastChannel construction
      // is unavailable despite the global being present.
    }
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key === LOGOUT_STORAGE_KEY && event.newValue !== null) {
      onLogout();
    }
  };

  window.addEventListener("storage", handleStorage);
  return () => window.removeEventListener("storage", handleStorage);
};
